import { BridgeClient } from 'app/service/bridge/BridgeClient';

/**
 * Creates a catalog selector API backed by the bridge transport.
 *
 * Covers:
 *   catalog_selectors.discover       — list registered selector keys + labels
 *   catalog_selectors.get_active     — current selector key + availability criteria
 *   catalog_selectors.set            — persist a new active selector key
 *   availability_rules.discover      — list registered availability rule keys + labels
 *   availability_criteria.get_active — current rule key + params
 *   availability_criteria.set        — persist new availability criteria
 */
export function createBridgeCatalogSelectorApi() {
  const client = new BridgeClient();

  return {
    /**
     * @returns {Promise<{ result: string, selectors: Array<{ key: string, label: string, is_active: boolean }> }>}
     */
    async discoverSelectors() {
      return client.sendMessageWithResponse('catalog_selectors.discover', {});
    },

    /**
     * @returns {Promise<{ result: string, selector_key: string, availability_criteria: object }>}
     */
    async getActive() {
      return client.sendMessageWithResponse('catalog_selectors.get_active', {});
    },

    /**
     * @param {string} selectorKey
     * @param {object} [config={}]
     * @returns {Promise<{ result: string }>}
     */
    async setSelector(selectorKey, config = {}) {
      return client.sendMessageWithResponse('catalog_selectors.set', {
        selector_key: selectorKey,
        config,
      });
    },

    /**
     * @returns {Promise<{ result: string, rules: Array<{ key: string, label: string, params_schema: object }> }>}
     */
    async discoverAvailabilityRules() {
      return client.sendMessageWithResponse('availability_rules.discover', {});
    },

    /**
     * @returns {Promise<{ result: string, rule_key: string, params: object }>}
     */
    async getActiveAvailabilityCriteria() {
      return client.sendMessageWithResponse('availability_criteria.get_active', {});
    },

    /**
     * @param {string} ruleKey
     * @param {object} [params={}]
     * @param {boolean} [fallbackWhenAbsent=true]
     * @returns {Promise<{ result: string }>}
     */
    async setAvailabilityCriteria(ruleKey, params = {}, fallbackWhenAbsent = true) {
      return client.sendMessageWithResponse('availability_criteria.set', {
        available_rule_key: ruleKey,
        rule_params: params,
        fallback_when_absent: fallbackWhenAbsent,
      });
    },
  };
}
